// aiSkills/costEstimator — Pure cost-estimation för ExecutionPlan + agent-
// pipeline. useAiCreditGating.preflightAndReserve kräver ett number-estimate
// innan LLM-call; denna modul räknar ut det deterministiskt baserat på
// skill-struktur.
//
// Ballpark-siffror (baserade på empiri från Lovable gateway + gpt-4o-mini):
// - plan_diagram:            ~50 credits (rubrik + 3-6 sheets)
// - create_sheet per sheet:  ~20 credits
// - batch_create_blocks:     10 + 2/block
// - batch_create_connectors: 5 + 1/edge
// - create_group per group:  ~10
// - create_cross_sheet_link: ~5 per link
// - validate_diagram:        ~15 (no LLM output, bara tool-result-parse)
// - critic-agent iteration:  ~80 (läser plan + genererar revised)
// - layout-agent iteration:  ~60
// - reviewer-agent:          ~80

import type { ExecutionPlan } from '@/lib/aiSkills/skillRegistry';
import type { AgentId } from '@/lib/aiAgents/agentRegistry';

export interface CostBreakdown {
  total: number;
  byStep: Array<{ tool: string; credits: number; reason: string }>;
  byAgent?: Record<AgentId, number>;
}

const PER_TOOL_BASE: Record<string, number> = {
  plan_diagram: 50,
  create_sheet: 20,
  batch_create_blocks: 10,
  batch_create_connectors: 5,
  create_group: 10,
  create_cross_sheet_link: 5,
  validate_diagram: 15,
  describe_block_connections: 10,
};

const PER_AGENT_BASE: Record<AgentId, number> = {
  planner: 60,
  critic: 80,
  builder: 20,  // builder paybar nästan allt via tools (se byStep-sum)
  layout: 60,
  reviewer: 80,
  'repo-cartographer': 100,
};

function countFromArgs(args: Record<string, unknown>, key: string): number {
  const v = args[key];
  return Array.isArray(v) ? v.length : 0;
}

/**
 * Estimera kostnad för en ExecutionPlan. Skalar med input-storlek — t.ex.
 * batch_create_blocks med 30 blocks kostar mer än med 3.
 */
export function estimateSkillCost(plan: ExecutionPlan): CostBreakdown {
  const byStep: CostBreakdown['byStep'] = [];
  for (const step of plan.steps) {
    const base = PER_TOOL_BASE[step.tool] ?? 10;
    let extra = 0;
    let reason = `base: ${base}`;
    if (step.tool === 'batch_create_blocks') {
      const n = countFromArgs(step.args, 'blocks');
      extra = n * 2;
      reason += `, ${n} blocks × 2`;
    } else if (step.tool === 'batch_create_connectors') {
      const n = countFromArgs(step.args, 'edges');
      extra = n * 1;
      reason += `, ${n} edges × 1`;
    }
    byStep.push({ tool: step.tool, credits: base + extra, reason });
  }
  return {
    total: byStep.reduce((sum, s) => sum + s.credits, 0),
    byStep,
  };
}

/**
 * Estimera kostnad för en full agent-pipeline. Tar ExecutionPlan (från
 * builder) + lista av agents som ska köra, och returnerar per-agent-cost
 * + total.
 */
export function estimatePipelineCost(
  plan: ExecutionPlan | null,
  agentIds: AgentId[],
): CostBreakdown {
  const stepBreakdown = plan ? estimateSkillCost(plan) : { total: 0, byStep: [] };
  const byAgent: Record<string, number> = {};
  let agentTotal = 0;
  for (const id of agentIds) {
    const cost = PER_AGENT_BASE[id];
    byAgent[id] = cost;
    agentTotal += cost;
  }
  return {
    total: stepBreakdown.total + agentTotal,
    byStep: stepBreakdown.byStep,
    byAgent: byAgent as Record<AgentId, number>,
  };
}

/**
 * Human-readable breakdown för UI.
 */
export function describeCost(breakdown: CostBreakdown): string {
  const lines: string[] = [`Total: ${breakdown.total} credits`];
  if (breakdown.byAgent) {
    lines.push('', 'Agents:');
    for (const [id, cost] of Object.entries(breakdown.byAgent)) {
      lines.push(`  ${id}: ${cost}`);
    }
  }
  lines.push('', 'Tool-steg:');
  for (const step of breakdown.byStep) {
    lines.push(`  ${step.tool}: ${step.credits} (${step.reason})`);
  }
  return lines.join('\n');
}
